import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, ApiError } from '../lib/api'
import { useToast } from '../lib/toast'
import { Modal } from './Modal'
import { EmojiPick } from './EmojiPick'
import { BroadcastAdmin } from './BroadcastAdmin'
import { activationOf, activationReach, EVENT_ICONS } from '@shared/types'
import { DAY_NAMES, DAY_STATUSES } from '@shared/types'
import type { ActivationStep, ActivityEvent, AdminFamily, AdminFamilyWeeks, AdminPantry, AdminWeekBoard, Category, FamilyPantryDiff, Feedback, FamilyStatus, PantryAddition, StarterAdminItem } from '@shared/types'
import { FEEDBACK_CATEGORIES } from '@shared/types'

/**
 * The admin area, ADMIN_EMAILS only. Three views behind a small sub-nav:
 *
 *   Overview  -- the numbers, the newest open feedback, the families waiting,
 *                each with a "View all" into its own page.
 *   Feedback  -- every submission, open first, filtered by status and kind,
 *                searchable, paged with "Show more".
 *   Families  -- every family, pending first, filtered by status, searchable
 *                by name or member, paged the same way.
 *   Pantry    -- the starter pantry every new family is seeded from, what
 *                families added on top of it (folded by name, ranked by how
 *                many), and Promote, which puts an addition into the starter
 *                for everyone who signs up from now on.
 *   Broadcast -- an announcement or a survey to every account holder, written
 *                in markdown, and every one that has already gone out.
 *
 * The admin's own family gets no buttons at all -- the server refuses those
 * too -- because one stray tap would lock them out.
 */
type View = 'overview' | 'activity' | 'feedback' | 'families' | 'pantry' | 'broadcast'
type FeedbackFilter = 'new' | 'done' | 'all'
type FamilyFilter = FamilyStatus | 'all'

const PAGE = 20
const PEEK = 3

const VIEWS: { id: View; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'activity', label: 'Activity' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'families', label: 'Families' },
  { id: 'pantry', label: 'Pantry' },
  { id: 'broadcast', label: 'Broadcast' },
]

export function AdminPage({
  ownFamilyId,
  categories,
  onError,
}: {
  ownFamilyId: string
  categories: Category[]
  onError: (err: unknown, fallback: string) => void
}) {
  const toast = useToast()
  const [view, setView] = useState<View>('overview')
  const [families, setFamilies] = useState<AdminFamily[] | null>(null)
  const [feedback, setFeedback] = useState<Feedback[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  /** The family whose Delete has been tapped once; the second tap is the real one. */
  const [arming, setArming] = useState<string | null>(null)
  /** The family whose pantry diff is open. */
  const [pantryOf, setPantryOf] = useState<AdminFamily | null>(null)
  /** The family whose boards are open. */
  const [weeksOf, setWeeksOf] = useState<AdminFamily | null>(null)
  /*
   * Held as an id rather than the row itself, so a reload after approving or
   * declining shows the new status instead of the stale copy that was clicked.
   */
  const [detailOf, setDetailOf] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [f, fb] = await Promise.all([api.adminFamilies(), api.adminFeedback()])
      setFamilies(f)
      setFeedback(fb)
    } catch (err) {
      onError(err, 'Could not load the admin page.')
    }
  }, [onError])

  useEffect(() => {
    void load()
  }, [load])

  async function markFeedback(id: string, status: 'new' | 'done') {
    try {
      await api.setFeedbackStatus(id, status)
      setFeedback((list) => list?.map((f) => (f.id === id ? { ...f, status } : f)) ?? null)
    } catch (err) {
      onError(err, 'That did not save.')
    }
  }

  async function decide(id: string, action: 'approve' | 'decline' | 'delete') {
    if (busy) return
    setBusy(id)
    try {
      if (action === 'approve') await api.approveFamily(id)
      else if (action === 'decline') await api.declineFamily(id)
      else {
        const r = await api.deleteFamily(id)
        toast.notify(
          `Deleted. ${r.accounts} account${r.accounts === 1 ? '' : 's'} and ${r.photos} photo${r.photos === 1 ? '' : 's'} went with it.`,
        )
      }
      setArming(null)
      await load()
    } catch (err) {
      onError(err, err instanceof ApiError ? err.message : 'That did not save.')
    } finally {
      setBusy(null)
    }
  }

  if (!families || !feedback) return <p className="muted">Loading…</p>

  const openFeedback = feedback.filter((f) => f.status === 'new')
  const pendingFamilies = families.filter((f) => f.status === 'pending')

  const familyCard = (f: AdminFamily) => (
    <FamilyCard
      key={f.id}
      family={f}
      own={f.id === ownFamilyId}
      busy={busy === f.id}
      arming={arming === f.id}
      onArm={(on) => setArming(on ? f.id : null)}
      onDecide={(a) => decide(f.id, a)}
      onPantry={() => setPantryOf(f)}
      onWeeks={() => setWeeksOf(f)}
      onOpen={() => setDetailOf(f.id)}
    />
  )
  const feedbackCard = (f: Feedback) => <FeedbackCard key={f.id} item={f} onMark={(s) => markFeedback(f.id, s)} />

  const detail = detailOf ? families.find((f) => f.id === detailOf) : null
  if (detailOf && !detail) {
    // Deleted from its own page -- there is nothing left to show.
    return (
      <div className="admin-page">
        <div className="empty">
          <span className="big" aria-hidden="true">
            🗑
          </span>
          That family is gone.
          <button className="btn sm" onClick={() => setDetailOf(null)}>
            All families
          </button>
        </div>
      </div>
    )
  }
  if (detail) {
    return (
      <div className="admin-page">
        <FamilyDetail
          family={detail}
          own={detail.id === ownFamilyId}
          busy={busy === detail.id}
          arming={arming === detail.id}
          onArm={(on) => setArming(on ? detail.id : null)}
          onDecide={(a) => decide(detail.id, a)}
          onPantry={() => setPantryOf(detail)}
          onWeeks={() => setWeeksOf(detail)}
          onBack={() => setDetailOf(null)}
          onError={onError}
        />
        {pantryOf && (
          <FamilyPantry family={pantryOf} categories={categories} onError={onError} onClose={() => setPantryOf(null)} />
        )}
        {weeksOf && (
          <FamilyWeeks family={weeksOf} categories={categories} onError={onError} onClose={() => setWeeksOf(null)} />
        )}
      </div>
    )
  }

  return (
    <div className="admin-page">
      <nav className="subnav" aria-label="Admin sections">
        {VIEWS.map((v) => (
          <button key={v.id} className="subnav-link" aria-current={view === v.id ? 'page' : undefined} onClick={() => setView(v.id)}>
            {v.label}
            {v.id === 'feedback' && openFeedback.length > 0 && <span className="subnav-count">{openFeedback.length}</span>}
            {v.id === 'families' && pendingFamilies.length > 0 && <span className="subnav-count">{pendingFamilies.length}</span>}
          </button>
        ))}
      </nav>

      {view === 'overview' && (
        <Overview
          families={families}
          feedback={feedback}
          pendingFamilies={pendingFamilies}
          openFeedback={openFeedback}
          familyCard={familyCard}
          feedbackCard={feedbackCard}
          onView={setView}
        />
      )}
      {view === 'activity' && <ActivityFeed onError={onError} onFamily={setDetailOf} />}
      {view === 'feedback' && <FeedbackList feedback={feedback} card={feedbackCard} />}
      {view === 'families' && <FamilyList families={families} card={familyCard} />}
      {view === 'pantry' && <PantryAdmin categories={categories} onError={onError} onFamily={(id) => setPantryOf(families.find((f) => f.id === id) ?? null)} />}
      {view === 'broadcast' && <BroadcastAdmin onError={onError} />}
      {pantryOf && <FamilyPantry family={pantryOf} categories={categories} onError={onError} onClose={() => setPantryOf(null)} />}
      {weeksOf && <FamilyWeeks family={weeksOf} categories={categories} onError={onError} onClose={() => setWeeksOf(null)} />}
    </div>
  )
}

/* ---- Overview ------------------------------------------------------------ */

function Overview({
  families,
  feedback,
  pendingFamilies,
  openFeedback,
  familyCard,
  feedbackCard,
  onView,
}: {
  families: AdminFamily[]
  feedback: Feedback[]
  pendingFamilies: AdminFamily[]
  openFeedback: Feedback[]
  familyCard: (f: AdminFamily) => ReactNode
  feedbackCard: (f: Feedback) => ReactNode
  onView: (v: View) => void
}) {
  const active = families.filter((f) => f.status === 'active').length
  const weekAgo = new Date(Date.now() - 7 * 86400e3).toISOString()
  const newThisWeek = families.filter((f) => f.createdAt > weekAgo).length
  /*
   * The one number that turns the per-family funnel into something to do:
   * families who were let in and then never placed a thing. Marked hot not
   * because it is an error but because it is the only stat here that is
   * waiting on someone -- the others are counts, this one is a list of
   * people to go and ask what happened.
   */
  const stalled = families.filter((f) => f.status === 'active' && f.placementCount === 0).length
  const recentFamilies = pendingFamilies.length ? pendingFamilies : families.filter((f) => f.status !== 'declined')

  return (
    <>
      <div className="stat-row">
        <Stat n={pendingFamilies.length} label="waiting for approval" hot={pendingFamilies.length > 0} onClick={() => onView('families')} />
        <Stat n={openFeedback.length} label="open feedback" hot={openFeedback.length > 0} onClick={() => onView('feedback')} />
        <Stat n={active} label={`active famil${active === 1 ? 'y' : 'ies'}`} onClick={() => onView('families')} />
        <Stat n={newThisWeek} label="signed up this week" onClick={() => onView('families')} />
        <Stat n={stalled} label="approved, never started" hot={stalled > 0} onClick={() => onView('families')} />
      </div>

      <section className="admin-section">
        <div className="page-head">
          <div>
            <h2>{pendingFamilies.length ? 'Waiting for approval' : 'Families'}</h2>
            <p>
              {pendingFamilies.length
                ? 'Approving switches on the assistant and photo uploads and emails everyone in the family.'
                : 'Nobody is waiting. The newest are here.'}
            </p>
          </div>
          <div className="spacer" />
          <button className="linklike" onClick={() => onView('families')}>
            View all {families.length} →
          </button>
        </div>
        {recentFamilies.length === 0 ? (
          <p className="muted">No families yet.</p>
        ) : (
          <ul className="admin-list">{recentFamilies.slice(0, PEEK).map(familyCard)}</ul>
        )}
        {recentFamilies.length > PEEK && (
          <p className="muted small admin-more">
            and {recentFamilies.length - PEEK} more.{' '}
            <button className="linklike" onClick={() => onView('families')}>
              View all
            </button>
          </p>
        )}
      </section>

      <section className="admin-section">
        <div className="page-head">
          <div>
            <h2>Open feedback</h2>
            <p>
              {openFeedback.length ? 'Newest first. ' : 'All caught up. '}
              Each one was also emailed to you with its screenshots.
            </p>
          </div>
          <div className="spacer" />
          <button className="linklike" onClick={() => onView('feedback')}>
            View all {feedback.length} →
          </button>
        </div>
        {openFeedback.length === 0 ? (
          <p className="muted">Nothing open. The Feedback tab on the right edge (or the 💬 on a phone) is where it comes from.</p>
        ) : (
          <ul className="admin-list">{openFeedback.slice(0, PEEK).map(feedbackCard)}</ul>
        )}
        {openFeedback.length > PEEK && (
          <p className="muted small admin-more">
            and {openFeedback.length - PEEK} more.{' '}
            <button className="linklike" onClick={() => onView('feedback')}>
              View all
            </button>
          </p>
        )}
      </section>
    </>
  )
}

function Stat({ n, label, hot, onClick }: { n: number; label: string; hot?: boolean; onClick: () => void }) {
  return (
    <button className={`stat ${hot ? 'hot' : ''}`} onClick={onClick}>
      <span className="stat-n">{n}</span>
      <span className="stat-label">{label}</span>
    </button>
  )
}

/* ---- Feedback ------------------------------------------------------------ */

/**
 * The rows themselves, shared by the Activity tab and by one family's page.
 *
 * `onFamily` is what makes a family name a link. A feed already narrowed to
 * one family passes nothing, because a link that reloads the page you are on
 * is a link that teaches people not to trust links.
 */
function FeedRows({ events, onFamily }: { events: ActivityEvent[]; onFamily?: (id: string) => void }) {
  return (
    <ol className="feed">
      {events.map((e) => (
        <li key={e.id} className={`feed-row ${e.kind === 'email_failed' ? 'bad' : ''}`}>
          <span className="feed-icon" aria-hidden="true">
            {EVENT_ICONS[e.kind] ?? '•'}
          </span>
          <span className="feed-text">
            <span className="feed-line">{e.summary}</span>
            <span className="feed-meta">
              <time dateTime={`${e.at.replace(' ', 'T')}Z`}>{whenText(e.at)}</time>
              {e.familyName && (
                <>
                  {' · '}
                  {e.familyId && !e.deleted && onFamily ? (
                    <button className="linklike" onClick={() => onFamily(e.familyId!)}>
                      {e.familyName}
                    </button>
                  ) : (
                    <span title={e.deleted ? 'This family has since been deleted' : undefined}>
                      {e.familyName}
                      {e.deleted && ' (deleted)'}
                    </span>
                  )}
                </>
              )}
              {e.actor && ` · ${e.actor}`}
              {e.detail && ` · ${e.detail}`}
            </span>
          </span>
        </li>
      ))}
    </ol>
  )
}

/** One request for a family's events, or for everything. */
function useEvents(family: string | undefined, onError: (err: unknown, fallback: string) => void) {
  const [events, setEvents] = useState<ActivityEvent[] | null>(null)
  useEffect(() => {
    let cancelled = false
    setEvents(null)
    api
      .adminEvents(family || undefined)
      .then((e) => !cancelled && setEvents(e))
      .catch((err) => !cancelled && onError(err, 'Could not load the activity feed.'))
    return () => {
      cancelled = true
    }
  }, [family, onError])
  return events
}

/**
 * What happened, newest first.
 *
 * It loads on its own rather than with the rest of the admin page: the other
 * three views are wanted every time this page opens, and this one is a
 * question you go looking for.
 */
function ActivityFeed({
  onError,
  onFamily,
}: {
  onError: (err: unknown, fallback: string) => void
  onFamily: (id: string) => void
}) {
  const events = useEvents(undefined, onError)
  if (!events) return <p className="muted">Loading…</p>
  if (events.length === 0) {
    return (
      <div className="empty">
        <span className="big" aria-hidden="true">
          🗒
        </span>
        Nothing recorded yet.
      </div>
    )
  }
  return <FeedRows events={events} onFamily={onFamily} />
}

/* ---- One family ---------------------------------------------------------- */

/**
 * Everything about one family on one screen: how far they got, what they
 * have, who is on the account, and their own slice of the feed.
 *
 * The question this page exists for is "did they actually start using it",
 * which the two numbers on the right of the funnel answer and the feed then
 * dates.
 */
function FamilyDetail({
  family: f,
  own,
  busy,
  arming,
  onArm,
  onDecide,
  onPantry,
  onWeeks,
  onBack,
  onError,
}: {
  family: AdminFamily
  own: boolean
  busy: boolean
  arming: boolean
  onArm: (on: boolean) => void
  onDecide: (a: 'approve' | 'decline' | 'delete') => void
  onPantry: () => void
  onWeeks: () => void
  onBack: () => void
  onError: (err: unknown, fallback: string) => void
}) {
  const events = useEvents(f.id, onError)
  const steps = activationOf(f)

  /*
   * When a step happened, if the feed happens to know. It only knows about
   * things that occurred after the feed shipped, so a missing date means
   * "before we started recording", never "it did not happen" -- the tick
   * itself comes from the tables and is always right.
   */
  const KIND_OF: Partial<Record<ActivationStep['key'], string>> = {
    onboarded: 'family_created',
    approved: 'approved',
    placed: 'first_week',
    full_week: 'full_week',
  }
  const whenOf = (key: ActivationStep['key']) => {
    const kind = KIND_OF[key]
    // Oldest matching row: the first time it happened, not the latest.
    const row = kind && [...(events ?? [])].reverse().find((e) => e.kind === kind)
    return row ? whenText(row.at) : ''
  }

  return (
    <div className="family-page">
      <button className="btn sm" onClick={onBack}>
        ← All families
      </button>

      <header className="family-head">
        <h1>
          {f.name} <span className={`pill ${f.status}`}>{f.status}</span>
        </h1>
        <p className="muted small">
          for {f.childName || '—'} · signed up {f.createdAt.slice(0, 10)}
          {f.approvedAt ? ` · approved ${f.approvedAt.slice(0, 10)} by ${f.approvedBy}` : ''}
        </p>
      </header>

      <section className="family-block">
        <h2>How far they got</h2>
        <ol className="funnel">
          {steps.map((s) => {
            const at = s.done ? whenOf(s.key) : ''
            return (
              <li key={s.key} className={`funnel-step ${s.done ? 'done' : 'not'}`}>
                <span className="funnel-mark" aria-hidden="true">
                  {s.done ? '✓' : '○'}
                </span>
                <span className="funnel-text">
                  <span className="funnel-label">{s.label}</span>
                  {/* Either half may be missing, so they are joined rather
                      than concatenated: an approved family with nothing to add
                      would otherwise read " · just now". */}
                  <span className="funnel-detail">{[s.detail, at].filter(Boolean).join(' · ')}</span>
                </span>
              </li>
            )
          })}
        </ol>
      </section>

      <section className="family-block">
        <h2>What they have</h2>
        <p className="muted small" style={{ margin: 0 }}>
          <Boards family={f} onOpen={onWeeks} />
          {' · '}
          <button type="button" className="linklike" onClick={onPantry}>
            Pantry ({f.itemCount} foods)
          </button>{' '}
          · {f.placementCount} food
          {f.placementCount === 1 ? '' : 's'} placed in all
        </p>
        <ul className="admin-members">
          {f.members.map((m) => (
            <li key={m.email}>
              {m.name} <span className="muted">&lt;{m.email}&gt;</span>
            </li>
          ))}
          {f.members.length === 0 && <li className="muted">nobody has an account yet</li>}
        </ul>
      </section>

      <section className="family-block">
        <h2>Activity</h2>
        {!events ? (
          <p className="muted">Loading…</p>
        ) : events.length === 0 ? (
          <p className="muted small">
            Nothing recorded for this family. Anything from before the feed shipped is not here.
          </p>
        ) : (
          <FeedRows events={events} />
        )}
      </section>

      <section className="family-block">
        <h2>Decide</h2>
        {own ? (
          <p className="muted small">This is your own family. There are no buttons here on purpose.</p>
        ) : arming ? (
          <div className="admin-actions arming">
            <span className="small">
              Delete <b>{f.name}</b>? Their pantry, weeks, photos and accounts go too. There is no undo.
            </span>
            <div className="row">
              <button className="btn danger sm" disabled={busy} onClick={() => onDecide('delete')}>
                {busy ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button className="btn ghost sm" onClick={() => onArm(false)}>
                Keep
              </button>
            </div>
          </div>
        ) : (
          <div className="row">
            {f.status !== 'active' && (
              <button className="btn primary sm" disabled={busy} onClick={() => onDecide('approve')}>
                Approve
              </button>
            )}
            {f.status !== 'declined' && (
              <button className="btn danger sm" disabled={busy} onClick={() => onDecide('decline')}>
                Decline
              </button>
            )}
            <button className="btn ghost sm" disabled={busy} onClick={() => onArm(true)}>
              Delete
            </button>
          </div>
        )}
      </section>
    </div>
  )
}

/** "3 minutes ago" down to the day, then the date. SQLite writes UTC. */
function whenText(at: string): string {
  const then = new Date(`${at.replace(' ', 'T')}Z`).getTime()
  if (Number.isNaN(then)) return at
  const mins = Math.round((Date.now() - then) / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days <= 7) return `${days} day${days === 1 ? '' : 's'} ago`
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function FeedbackList({ feedback, card }: { feedback: Feedback[]; card: (f: Feedback) => ReactNode }) {
  const [filter, setFilter] = useState<FeedbackFilter>('new')
  const [kind, setKind] = useState<string>('')
  const [q, setQ] = useState('')
  const [limit, setLimit] = useState(PAGE)

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return feedback.filter(
      (f) =>
        (filter === 'all' || f.status === filter) &&
        (!kind || f.category === kind) &&
        (!needle || [f.message, f.userName, f.userEmail, f.familyName, f.page].some((s) => s.toLowerCase().includes(needle))),
    )
  }, [feedback, filter, kind, q])

  const counts = {
    new: feedback.filter((f) => f.status === 'new').length,
    done: feedback.filter((f) => f.status === 'done').length,
    all: feedback.length,
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Feedback</h1>
          <p>
            {counts.new} open{counts.done ? `, ${counts.done} done` : ''}. Each one was also emailed to you with its screenshots.
          </p>
        </div>
      </div>

      <div className="admin-filters">
        <div className="row wrap" role="group" aria-label="Status">
          {(['new', 'done', 'all'] as FeedbackFilter[]).map((s) => (
            <button
              key={s}
              className="btn sm"
              aria-pressed={filter === s}
              onClick={() => {
                setFilter(s)
                setLimit(PAGE)
              }}
            >
              {s === 'new' ? 'Open' : s === 'done' ? 'Done' : 'All'} <span className="muted">{counts[s]}</span>
            </button>
          ))}
        </div>
        <div className="row wrap" role="group" aria-label="Kind">
          {FEEDBACK_CATEGORIES.map((c) => (
            <button
              key={c.value}
              className="btn sm"
              aria-pressed={kind === c.value}
              onClick={() => {
                setKind(kind === c.value ? '' : c.value)
                setLimit(PAGE)
              }}
            >
              {c.emoji} {c.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="admin-search"
          placeholder="Search message, name, family…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setLimit(PAGE)
          }}
          aria-label="Search feedback"
        />
      </div>

      {shown.length === 0 ? (
        <p className="muted">
          {feedback.length === 0
            ? 'Nothing yet. The Feedback tab on the right edge (or the 💬 on a phone) is where it comes from.'
            : 'Nothing matches.'}
        </p>
      ) : (
        <ul className="admin-list">{shown.slice(0, limit).map(card)}</ul>
      )}
      <ShowMore total={shown.length} limit={limit} onMore={() => setLimit((n) => n + PAGE)} />
    </>
  )
}

function FeedbackCard({ item: f, onMark }: { item: Feedback; onMark: (s: 'new' | 'done') => void }) {
  const cat = FEEDBACK_CATEGORIES.find((k) => k.value === f.category)
  return (
    <li className={`admin-card feedback-card ${f.status === 'done' ? 'done' : ''}`}>
      <div className="admin-main">
        <div className="title">
          {cat ? `${cat.emoji} ${cat.label}` : 'Feedback'}
          {f.status === 'done' && <span className="pill">done</span>}
        </div>
        <div className="muted small">
          {f.userName} <span className="muted">&lt;{f.userEmail}&gt;</span> · {f.familyName}
        </div>
        <p className="feedback-message">{f.message}</p>
        {f.imageCount > 0 && (
          <div className="feedback-shots">
            {Array.from({ length: f.imageCount }, (_, i) => (
              <a key={i} className="feedback-shot" href={`/api/admin/feedback/${f.id}/images/${i + 1}`} target="_blank" rel="noreferrer">
                <img src={`/api/admin/feedback/${f.id}/images/${i + 1}`} alt={`Screenshot ${i + 1}`} loading="lazy" />
              </a>
            ))}
          </div>
        )}
        <div className="muted small">
          {f.createdAt.slice(0, 16).replace('T', ' ')}
          {f.page ? ` · ${f.page}` : ''}
          {f.weekStart ? ` · week of ${f.weekStart}` : ''}
          {f.viewport ? ` · ${f.viewport}` : ''}
        </div>
      </div>
      <div className="admin-actions">
        {f.status === 'new' ? (
          <button className="btn sm" onClick={() => onMark('done')}>
            Done
          </button>
        ) : (
          <button className="btn ghost sm" onClick={() => onMark('new')}>
            Reopen
          </button>
        )}
      </div>
    </li>
  )
}

/* ---- Families ------------------------------------------------------------ */

function FamilyList({ families, card }: { families: AdminFamily[]; card: (f: AdminFamily) => ReactNode }) {
  const [filter, setFilter] = useState<FamilyFilter>('all')
  const [q, setQ] = useState('')
  const [limit, setLimit] = useState(PAGE)

  const counts = {
    all: families.length,
    pending: families.filter((f) => f.status === 'pending').length,
    active: families.filter((f) => f.status === 'active').length,
    declined: families.filter((f) => f.status === 'declined').length,
  }

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return families.filter(
      (f) =>
        (filter === 'all' || f.status === filter) &&
        (!needle ||
          f.name.toLowerCase().includes(needle) ||
          f.childName.toLowerCase().includes(needle) ||
          f.members.some((m) => m.name.toLowerCase().includes(needle) || m.email.toLowerCase().includes(needle))),
    )
  }, [families, filter, q])

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Families</h1>
          <p>
            {counts.all} in all{counts.pending ? `, ${counts.pending} waiting for approval` : ''}. Approving switches on the
            assistant and photo uploads and emails everyone in the family.
          </p>
        </div>
      </div>

      <div className="admin-filters">
        <div className="row wrap" role="group" aria-label="Status">
          {(['all', 'pending', 'active', 'declined'] as FamilyFilter[]).map((s) => (
            <button
              key={s}
              className="btn sm"
              aria-pressed={filter === s}
              onClick={() => {
                setFilter(s)
                setLimit(PAGE)
              }}
            >
              {s === 'all' ? 'All' : s[0].toUpperCase() + s.slice(1)} <span className="muted">{counts[s]}</span>
            </button>
          ))}
        </div>
        <input
          type="search"
          className="admin-search"
          placeholder="Search family, kid, member…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setLimit(PAGE)
          }}
          aria-label="Search families"
        />
      </div>

      {shown.length === 0 ? (
        <p className="muted">{families.length === 0 ? 'No families yet.' : 'Nothing matches.'}</p>
      ) : (
        <ul className="admin-list">{shown.slice(0, limit).map(card)}</ul>
      )}
      <ShowMore total={shown.length} limit={limit} onMore={() => setLimit((n) => n + PAGE)} />
    </>
  )
}

/**
 * How far this family got, as four dots.
 *
 * The two questions worth asking about a family -- did they place a food, did
 * they finish a week -- are the last two, so the strip reads left to right and
 * the interesting part is at the end. The words are in the title attribute and
 * the screen-reader label rather than on screen: a list of twenty families
 * needs a shape you can scan, not four sentences twenty times.
 */
function Reach({ family: f }: { family: AdminFamily }) {
  const steps = activationOf(f)
  const reach = activationReach(f)
  return (
    <p className="reach" title={steps.map((s) => `${s.done ? '✓' : '○'} ${s.label}`).join('\n')}>
      <span className="sr-only">
        {reach} of {steps.length} steps: {steps.filter((s) => s.done).map((s) => s.label).join(', ') || 'none'}
      </span>
      {steps.map((s) => (
        <span key={s.key} className={`reach-dot ${s.done ? 'on' : ''}`} aria-hidden="true" />
      ))}
      <span className="reach-label" aria-hidden="true">
        {f.fullWeekCount > 0
          ? 'planned a whole week'
          : f.placementCount > 0
            ? 'placed food, never finished a week'
            : f.status === 'active'
              ? 'approved, never placed a food'
              : 'not approved yet'}
      </span>
    </p>
  )
}

function FamilyCard({
  family: f,
  own,
  busy,
  arming,
  onArm,
  onDecide,
  onPantry,
  onWeeks,
  onOpen,
}: {
  family: AdminFamily
  own: boolean
  busy: boolean
  arming: boolean
  onArm: (on: boolean) => void
  onDecide: (a: 'approve' | 'decline' | 'delete') => void
  onPantry: () => void
  onWeeks: () => void
  onOpen: () => void
}) {
  return (
    <li className={`admin-card status-${f.status}`}>
      <div className="admin-main">
        <div className="title">
          <button type="button" className="linklike" onClick={onOpen}>
            {f.name}
          </button>{' '}
          <span className={`pill ${f.status}`}>{f.status}</span>
        </div>
        <div className="muted small">
          for {f.childName || '—'} ·{' '}
          {/*
            * Two links, and they used to sit flush against each other:
            * "Boards80 foods" read as one word and nobody found the boards.
            * A separator between them, and each says what it opens.
            */}
          <Boards family={f} onOpen={onWeeks} />
          {' · '}
          <button type="button" className="linklike" onClick={onPantry}>
            Pantry ({f.itemCount} foods)
          </button>{' '}
          · signed up {f.createdAt.slice(0, 10)}
          {f.approvedAt ? ` · approved ${f.approvedAt.slice(0, 10)} by ${f.approvedBy}` : ''}
        </div>
        <Reach family={f} />
        <ul className="admin-members">
          {f.members.map((m) => (
            <li key={m.email}>
              {m.name} <span className="muted">&lt;{m.email}&gt;</span>
            </li>
          ))}
          {f.members.length === 0 && <li className="muted">nobody has an account yet</li>}
        </ul>
      </div>
      {own ? (
        <div className="admin-actions">
          <span className="muted small">yours</span>
        </div>
      ) : arming ? (
        <div className="admin-actions arming">
          <span className="small">
            Delete <b>{f.name}</b>? Their pantry, weeks, photos and accounts go too. There is no undo.
          </span>
          <div className="row">
            <button className="btn danger sm" disabled={busy} onClick={() => onDecide('delete')}>
              {busy ? 'Deleting…' : 'Yes, delete'}
            </button>
            <button className="btn ghost sm" onClick={() => onArm(false)}>
              Keep
            </button>
          </div>
        </div>
      ) : (
        <div className="admin-actions">
          {f.status !== 'active' && (
            <button className="btn primary sm" disabled={busy} onClick={() => onDecide('approve')}>
              Approve
            </button>
          )}
          {f.status !== 'declined' && (
            <button className="btn danger sm" disabled={busy} onClick={() => onDecide('decline')}>
              Decline
            </button>
          )}
          <button className="btn ghost sm" disabled={busy} onClick={() => onArm(true)}>
            Delete…
          </button>
        </div>
      )}
    </li>
  )
}

/**
 * The way into a family's boards, or the reason there is no way in.
 *
 * A family with nothing planned used to get the same link as a family with
 * four weeks, and the click it cost you ended at "No weeks started yet". The
 * count was further along the same line, which nobody reads before clicking
 * the thing in front of them. So the count sits on the control: a link when
 * there is something behind it, plain text when there is not.
 */
function Boards({ family, onOpen }: { family: AdminFamily; onOpen: () => void }) {
  if (family.weekCount === 0) return <span className="admin-none">No boards yet</span>
  return (
    <button type="button" className="linklike" onClick={onOpen}>
      Boards ({family.weekCount} week{family.weekCount === 1 ? '' : 's'})
    </button>
  )
}

/* ---- A family's boards --------------------------------------------------- */

/**
 * What a family actually put on the week.
 *
 * The families list says whether somebody planned; the number alone cannot
 * tell a week that works from a week abandoned after one food. This is the
 * board as they built it: five days, the foods in each, and the days they
 * marked off.
 *
 * Note bodies are not fetched. One parent writing to the other about their
 * own child is not what "see how it is used" needs, and the count says
 * whether the feature is used at all.
 */
function FamilyWeeks({
  family,
  categories,
  onError,
  onClose,
}: {
  family: AdminFamily
  categories: Category[]
  onError: (err: unknown, fallback: string) => void
  onClose: () => void
}) {
  const [data, setData] = useState<AdminFamilyWeeks | null>(null)
  /*
   * Which week is open, per child. A single id here looked fine with one
   * child and blanked the other's board the moment a family had two:
   * picking a week for one made the other's filter match nothing.
   */
  const [openWeek, setOpenWeek] = useState<Record<string, string>>({})

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const d = await api.familyWeeks(family.id)
        if (!live) return
        setData(d)
        // For each child, open their newest week that has anything on it, so
        // the common case is one click shorter and an empty week never greets
        // you.
        const first: Record<string, string> = {}
        for (const ch of d.children) {
          const best = ch.weeks.find((w) => w.days.some((day) => day.items.length > 0)) ?? ch.weeks[0]
          if (best) first[ch.id] = best.id
        }
        setOpenWeek(first)
      } catch (err) {
        onError(err, 'Could not load those weeks.')
      }
    })()
    return () => {
      live = false
    }
  }, [family.id, onError])

  const cat = (id: string) => categories.find((c) => c.id === id)

  /*
   * The order the board itself uses, which is category order: the main
   * first, and the lunchbox before the snack bag, because categories
   * sharing a container sit next to each other in this list.
   *
   * The API hands placements back in `sort_order` -- the order they were
   * dropped, which is the order somebody thought of them. Reading a week
   * here means comparing it against the board they built, so it has to look
   * like the board they built.
   */
  const rank = useMemo(() => new Map(categories.map((c, i) => [c.id, i])), [categories])
  const packedOrder = (items: AdminWeekBoard['days'][number]['items']) =>
    // Stable, so two foods in the same slot keep the order they were placed.
    [...items].sort((a, b) => (rank.get(a.categoryId) ?? 99) - (rank.get(b.categoryId) ?? 99))
  const withWeeks = data?.children.filter((ch) => ch.weeks.length) ?? []
  const empty = data && withWeeks.length === 0

  return (
    <Modal
      title={`${family.name} · boards`}
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      {!data ? (
        <p className="muted">Loading…</p>
      ) : empty ? (
        <p className="muted" style={{ margin: 0 }}>
          {data.children.length
            ? 'No weeks started yet. They have a pantry but have not opened a week.'
            : 'No children yet, so there is nothing to plan.'}
        </p>
      ) : (
        <div className="stack">
          {withWeeks.map((child) => (
            <section key={child.id}>
              {withWeeks.length > 1 && <h3 className="diff-head">{child.name}</h3>}
              <div className="admin-week-tabs">
                {child.weeks.map((w) => {
                  const packed = w.days.filter((d) => d.items.length > 0).length
                  return (
                    <button
                      key={w.id}
                      type="button"
                      className={`admin-week-tab${openWeek[child.id] === w.id ? ' on' : ''}`}
                      onClick={() => setOpenWeek((o) => ({ ...o, [child.id]: w.id }))}
                    >
                      <b>{w.startDate}</b>
                      <span>
                        {packed ? `${packed} day${packed === 1 ? '' : 's'}` : 'empty'}
                        {w.noteCount ? ` · ${w.noteCount} note${w.noteCount === 1 ? '' : 's'}` : ''}
                      </span>
                    </button>
                  )
                })}
              </div>

              {child.weeks
                .filter((w) => w.id === openWeek[child.id])
                .map((w) => (
                  <div key={w.id}>
                    <p className="muted small" style={{ margin: '10px 0' }}>
                      {w.days.reduce((n, d) => n + d.items.length, 0)} food
                      {w.days.reduce((n, d) => n + d.items.length, 0) === 1 ? '' : 's'} across{' '}
                      {w.days.filter((d) => d.items.length).length} day
                      {w.days.filter((d) => d.items.length).length === 1 ? '' : 's'}
                      {w.updatedAt ? ` · last touched ${w.updatedAt.slice(0, 16)}` : ''}
                      {w.updatedBy ? ` by ${w.updatedBy}` : ''}
                      {w.reviewState !== 'planning' ? ` · ${w.reviewState.replace('_', ' ')}` : ''}
                    </p>
                    <div className="admin-board">
                      {DAY_NAMES.map((name, i) => {
                        const day = w.days.find((d) => d.dayOfWeek === i + 1)
                        const off = day && day.status !== 'packing'
                        return (
                          <div className="admin-day" key={name}>
                            <h4>{name.slice(0, 3)}</h4>
                            {off ? (
                              <p className="admin-off">
                                {DAY_STATUSES.find((s) => s.value === day.status)?.emoji}{' '}
                                {day.label || DAY_STATUSES.find((s) => s.value === day.status)?.label}
                              </p>
                            ) : !day || day.items.length === 0 ? (
                              <p className="admin-off muted">empty</p>
                            ) : (
                              packedOrder(day.items).map((it) => (
                                <div
                                  className="admin-food"
                                  key={it.id}
                                  style={{ borderLeftColor: cat(it.categoryId)?.color ?? 'var(--line)' }}
                                  title={cat(it.categoryId)?.name ?? ''}
                                >
                                  <span aria-hidden="true">{it.emoji || '·'}</span>
                                  {it.name}
                                </div>
                              ))
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
            </section>
          ))}
          {data.truncated > 0 && (
            <p className="muted small" style={{ margin: 0 }}>
              {data.truncated} older week{data.truncated === 1 ? '' : 's'} not shown.
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}

/* ---- Pantry ------------------------------------------------------------- */

function PantryAdmin({
  categories,
  onError,
  onFamily,
}: {
  categories: Category[]
  onError: (err: unknown, fallback: string) => void
  onFamily: (id: string) => void
}) {
  const toast = useToast()
  const [data, setData] = useState<AdminPantry | null>(null)
  const [min, setMin] = useState<1 | 2 | 3>(1)
  /*
   * Off by default: the overlaps are worth seeing, because a food families
   * keep re-typing usually means the starter row is named something they do
   * not search for. This hides them when the job is picking what to add.
   */
  const [onlyNew, setOnlyNew] = useState(false)
  const [showRetired, setShowRetired] = useState(false)
  /** Rows retired on this visit stay in place, so the Restore button is right where Retire was. */
  const [touched, setTouched] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState<string | null>(null)
  const [limit, setLimit] = useState(PAGE)

  const load = useCallback(async () => {
    try {
      setData(await api.adminPantry())
    } catch (err) {
      onError(err, 'Could not load the pantry.')
    }
  }, [onError])

  useEffect(() => {
    void load()
  }, [load])

  async function promote(a: PantryAddition) {
    if (busy) return
    setBusy(a.name)
    try {
      const r = await api.promoteStarter({ name: a.name, categoryId: a.categoryId, emoji: a.emoji })
      toast.notify(
        `${a.emoji ? `${a.emoji} ` : ''}${a.name} is in the starter pantry now${r.stamped ? ` (and ${r.stamped} existing ${r.stamped === 1 ? 'copy' : 'copies'} counted as it)` : ''}.`,
      )
      await load()
    } catch (err) {
      onError(err, err instanceof ApiError ? err.message : 'That did not save.')
    } finally {
      setBusy(null)
    }
  }

  async function update(item: StarterAdminItem, patch: { name?: string; emoji?: string; retired?: boolean }) {
    if (busy) return
    setBusy(item.id)
    try {
      const r = await api.updateStarter(item.id, patch)
      setData((d) => (d && r.item ? { ...d, starter: d.starter.map((s) => (s.id === item.id ? r.item! : s)) } : d))
      if (patch.retired !== undefined) {
        setTouched((t) => new Set(t).add(item.id))
        toast.notify(patch.retired ? `${item.name} retired. New families will not get it.` : `${item.name} is back in the starter.`)
      }
    } catch (err) {
      onError(err, err instanceof ApiError ? err.message : 'That did not save.')
    } finally {
      setBusy(null)
    }
  }

  if (!data) return <p className="muted">Loading…</p>

  const cat = (id: string) => categories.find((c) => c.id === id)
  const live = data.starter.filter((s) => !s.retired)
  const retired = data.starter.filter((s) => s.retired)
  const candidates = data.additions.filter((a) => a.count >= min && (!onlyNew || a.similar.length === 0))

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Pantry</h1>
          <p>
            {live.length} starter foods seed every new family. Below them, what {data.families} famil{data.families === 1 ? 'y' : 'ies'}{' '}
            added on top. Promoting one puts it in the starter for everyone who signs up from now on; nobody’s pantry changes.
          </p>
        </div>
      </div>

      {/* Named so a test can tell these rows from the starter list below,
          which shares their class. */}
      <section className="admin-section" data-section="additions">
        <div className="page-head">
          <div>
            <h2>Added by families</h2>
            <p>Folded by name, one vote per family, most-added first.</p>
          </div>
          <div className="spacer" />
          <div className="row" role="group" aria-label="At least">
            {([1, 2, 3] as const).map((n) => (
              <button
                key={n}
                className="btn sm"
                aria-pressed={min === n}
                onClick={() => {
                  setMin(n)
                  setLimit(PAGE)
                }}
              >
                {n === 1 ? 'Any' : `${n}+ families`}
              </button>
            ))}
            <button
              className="btn sm"
              aria-pressed={onlyNew}
              title="Hide anything sharing a word with a food already in the starter list"
              onClick={() => {
                setOnlyNew((v) => !v)
                setLimit(PAGE)
              }}
            >
              Nothing like it yet
            </button>
          </div>
        </div>
        {candidates.length === 0 ? (
          <p className="muted">{data.additions.length === 0 ? 'Nothing beyond the starter yet.' : 'Nothing added by that many families yet.'}</p>
        ) : (
          <ul className="admin-list starter-list">
            {candidates.slice(0, limit).map((a) => (
              <li key={a.name} className="admin-card starter-admin-row">
                <span className="starter-emoji" aria-hidden="true">
                  {a.emoji || '·'}
                </span>
                <div className="admin-main">
                  <div className="title">
                    {a.name}
                    <span className="pill">{cat(a.categoryId)?.name ?? a.categoryId}</span>
                  </div>
                  <div className="muted small">
                    {a.count} famil{a.count === 1 ? 'y' : 'ies'}:{' '}
                    {a.families.slice(0, 6).map((f, i) => (
                      <span key={f.id}>
                        {i > 0 && ', '}
                        <button type="button" className="linklike" onClick={() => onFamily(f.id)}>
                          {f.name}
                        </button>
                      </span>
                    ))}
                    {a.families.length > 6 && ` and ${a.families.length - 6} more`}
                  </div>
                  {/*
                    An exact name match never reaches this list at all. These
                    are the near misses -- Carrots against Carrot Sticks --
                    which no rule can settle, so they are shown rather than
                    guessed at.
                  */}
                  {a.similar.length > 0 && (
                    <div className="muted small starter-similar">
                      Already in the starter list: {a.similar.join(' · ')}
                    </div>
                  )}
                </div>
                <div className="admin-actions">
                  <button
                    className={`btn sm ${a.similar.length === 0 ? 'primary' : ''}`}
                    disabled={busy === a.name}
                    onClick={() => promote(a)}
                  >
                    {busy === a.name ? 'Promoting…' : a.similar.length > 0 ? 'Promote anyway' : 'Promote'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        <ShowMore total={candidates.length} limit={limit} onMore={() => setLimit((n) => n + PAGE)} />
      </section>

      <section className="admin-section">
        <div className="page-head">
          <div>
            <h2>Starter pantry</h2>
            <p>
              What a new family sees in onboarding, by category. “Removed by” counts families that were seeded with it and no longer have
              it. Retiring a food stops seeding it; families that have it keep it.
            </p>
          </div>
          <div className="spacer" />
          {retired.length > 0 && (
            <button className="btn sm" aria-pressed={showRetired} onClick={() => setShowRetired((v) => !v)}>
              {showRetired ? 'Hide retired' : `Show ${retired.length} retired`}
            </button>
          )}
        </div>
        {categories.map((c) => {
          const rows = data.starter.filter((s) => s.categoryId === c.id && (showRetired || !s.retired || touched.has(s.id)))
          if (rows.length === 0) return null
          return (
            <div key={c.id} className="starter-cat-admin">
              <h3>
                <span aria-hidden="true">{c.emoji}</span> {c.name} <span className="muted">· {rows.filter((r) => !r.retired).length}</span>
              </h3>
              <ul className="admin-list starter-list">
                {rows.map((s) => (
                  <StarterRow key={s.id} item={s} busy={busy === s.id} onUpdate={(patch) => update(s, patch)} />
                ))}
              </ul>
            </div>
          )
        })}
      </section>
    </>
  )
}

function StarterRow({
  item: s,
  busy,
  onUpdate,
}: {
  item: StarterAdminItem
  busy: boolean
  onUpdate: (patch: { name?: string; emoji?: string; retired?: boolean }) => void
}) {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(s.name)
  const removed = Math.max(0, s.seededTo - s.keptBy)

  return (
    <li className={`admin-card starter-admin-row ${s.retired ? 'done' : ''}`}>
      <EmojiPick value={s.emoji} label={`Emoji for ${s.name}`} onChange={(emoji) => onUpdate({ emoji })} />
      <div className="admin-main">
        {editing ? (
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault()
              if (name.trim() && name.trim() !== s.name) onUpdate({ name: name.trim() })
              setEditing(false)
            }}
          >
            <input className="admin-search" value={name} maxLength={60} autoFocus aria-label={`Rename ${s.name}`} onChange={(e) => setName(e.target.value)} />
            <button className="btn sm" type="submit">
              Save
            </button>
            <button
              className="btn ghost sm"
              type="button"
              onClick={() => {
                setName(s.name)
                setEditing(false)
              }}
            >
              Cancel
            </button>
          </form>
        ) : (
          <div className="title">
            <button type="button" className="starter-name" title="Rename" onClick={() => setEditing(true)}>
              {s.name}
            </button>
            {s.retired && <span className="pill">retired</span>}
            {s.addedBy && !s.retired && <span className="pill active">promoted</span>}
          </div>
        )}
        <div className="muted small">
          {s.seededTo === 0
            ? 'no family seeded with it yet'
            : removed === 0
              ? `kept by all ${s.seededTo}`
              : `removed by ${removed} of ${s.seededTo}`}
          {s.addedBy ? ` · promoted ${s.createdAt.slice(0, 10)} by ${s.addedBy}` : ''}
        </div>
      </div>
      <div className="admin-actions">
        {s.retired ? (
          <button className="btn sm" disabled={busy} onClick={() => onUpdate({ retired: false })}>
            Restore
          </button>
        ) : (
          <button className="btn ghost sm" disabled={busy} onClick={() => onUpdate({ retired: true })}>
            Retire
          </button>
        )}
      </div>
    </li>
  )
}

/** One family's pantry against the starter: added, removed, renamed. Promote straight from here. */
function FamilyPantry({
  family,
  categories,
  onError,
  onClose,
}: {
  family: AdminFamily
  categories: Category[]
  onError: (err: unknown, fallback: string) => void
  onClose: () => void
}) {
  const toast = useToast()
  const [diff, setDiff] = useState<FamilyPantryDiff | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setDiff(await api.familyPantry(family.id))
    } catch (err) {
      onError(err, 'Could not load that pantry.')
    }
  }, [family.id, onError])

  useEffect(() => {
    void load()
  }, [load])

  async function promote(a: FamilyPantryDiff['added'][number]) {
    if (busy) return
    setBusy(a.id)
    try {
      await api.promoteStarter({ name: a.name, categoryId: a.categoryId, emoji: a.emoji })
      toast.notify(`${a.emoji ? `${a.emoji} ` : ''}${a.name} is in the starter pantry now.`)
      await load()
    } catch (err) {
      onError(err, err instanceof ApiError ? err.message : 'That did not save.')
    } finally {
      setBusy(null)
    }
  }

  const cat = (id: string) => categories.find((c) => c.id === id)?.name ?? id

  return (
    <Modal title={`${family.name} · pantry`} onClose={onClose} footer={<button className="btn" onClick={onClose}>Close</button>}>
      {!diff ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="stack">
          <p className="muted" style={{ margin: 0, fontSize: 13.5 }}>
            {diff.childName ? `${diff.childName}’s pantry: ` : ''}
            {diff.kept} starter food{diff.kept === 1 ? '' : 's'} kept, {diff.added.length} added, {diff.removed.length} removed
            {diff.edited.length ? `, ${diff.edited.length} renamed` : ''}.
          </p>

          <section>
            <h3 className="diff-head">Added ({diff.added.length})</h3>
            {diff.added.length === 0 ? (
              <p className="muted small">Nothing beyond the starter.</p>
            ) : (
              <ul className="diff-list">
                {diff.added.map((a) => (
                  <li key={a.id}>
                    <span className="starter-emoji" aria-hidden="true">
                      {a.emoji || '·'}
                    </span>
                    <span className="diff-name">{a.name}</span>
                    <span className="muted small">
                      {cat(a.categoryId)} · {a.createdAt.slice(0, 10)}
                    </span>
                    <button className="btn sm" disabled={busy === a.id} onClick={() => promote(a)}>
                      Promote
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="diff-head">Removed ({diff.removed.length})</h3>
            {diff.removed.length === 0 ? (
              <p className="muted small">They kept every starter food.</p>
            ) : (
              <ul className="diff-list">
                {diff.removed.map((r) => (
                  <li key={r.id} className="removed">
                    <span className="starter-emoji" aria-hidden="true">
                      {r.emoji || '·'}
                    </span>
                    <span className="diff-name">{r.name}</span>
                    <span className="muted small">{cat(r.categoryId)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {diff.edited.length > 0 && (
            <section>
              <h3 className="diff-head">Renamed ({diff.edited.length})</h3>
              <ul className="diff-list">
                {diff.edited.map((e) => (
                  <li key={e.starterId}>
                    <span className="starter-emoji" aria-hidden="true">
                      ✎
                    </span>
                    <span className="diff-name">{e.name}</span>
                    <span className="muted small">was {e.starterName}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Modal>
  )
}

/* ---- Shared -------------------------------------------------------------- */

function ShowMore({ total, limit, onMore }: { total: number; limit: number; onMore: () => void }) {
  if (total <= limit) return null
  return (
    <div className="admin-more row">
      <span className="muted small">
        Showing {limit} of {total}.
      </span>
      <button className="btn sm" onClick={onMore}>
        Show {Math.min(PAGE, total - limit)} more
      </button>
    </div>
  )
}
